import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, ExternalLink, Filter, Sparkles, ArrowRight, Search, X, Bookmark, Radio, Users, GitBranch, Cpu, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "all", label: "Tout", icon: Filter, color: "#FAF0DE" },
  { key: "article", label: "Articles", icon: Radio, color: "#FF8C42" },
  { key: "expert", label: "Experts", icon: Users, color: "#FFD600" },
  { key: "community", label: "Communautés", icon: Cpu, color: "#FFA85C" },
  { key: "update", label: "Updates Google", icon: GitBranch, color: "#4EA8FF" },
];

const RANGES = [
  { key: "", label: "Toutes dates" },
  { key: "1", label: "24 h" },
  { key: "7", label: "7 j" },
  { key: "30", label: "30 j" },
];

function categoryFor(type) {
  return CATEGORIES.find((c) => c.key === type) || CATEGORIES[1];
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}j`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [trend, setTrend] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [range, setRange] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/feed/tags");
        setTags(data.tags || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter !== "all") params.set("source_type", filter);
        if (debouncedQ) params.set("q", debouncedQ);
        if (range) params.set("since_days", range);
        if (tag) params.set("tag", tag);
        const [feedRes, trendRes] = await Promise.all([
          api.get(`/feed?${params.toString()}`),
          api.get("/trends/today"),
        ]);
        setItems(feedRes.data.items || []);
        setTrend(trendRes.data.trend);
      } finally {
        setLoading(false);
      }
    })();
  }, [filter, debouncedQ, range, tag]);

  const subscribe = async () => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const toggleBookmark = async (item) => {
    if (!user) return;
    try {
      if (item.bookmarked) {
        await api.delete(`/bookmarks/${item.item_id}`);
        setItems((x) => x.map((i) => (i.item_id === item.item_id ? { ...i, bookmarked: false } : i)));
        toast.success("Retiré des favoris");
      } else {
        await api.post("/bookmarks", { item_id: item.item_id });
        setItems((x) => x.map((i) => (i.item_id === item.item_id ? { ...i, bookmarked: true } : i)));
        toast.success("Ajouté aux favoris");
      }
    } catch {
      toast.error("Erreur");
    }
  };

  // Group items by source_type for the horizontal category rails
  const grouped = {
    article: items.filter((i) => i.source_type === "article"),
    expert: items.filter((i) => i.source_type === "expert"),
    community: items.filter((i) => i.source_type === "community"),
    update: items.filter((i) => i.source_type === "update"),
  };

  const showGrouped = filter === "all" && !debouncedQ && !range && !tag;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      {/* Hero header */}
      <div className="mb-10 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FAF0DE]/40 mb-3">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-none" data-testid="dashboard-title">
            Bonjour, <span className="text-shimmer">{user?.name?.split(" ")[0] || "invité"}</span>.
          </h1>
          <p className="text-[#FAF0DE]/50 mt-3 text-lg">Voici ce qui bouge dans le SEO mondial aujourd'hui.</p>
        </div>
        <div className="md:col-span-4 grid grid-cols-3 gap-3">
          {[
            { c: "#FF8C42", k: grouped.article.length + grouped.community.length, l: "Articles" },
            { c: "#FFD600", k: grouped.expert.length, l: "Experts" },
            { c: "#4EA8FF", k: grouped.update.length, l: "Updates" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl bg-[#132038]/60 border border-[#FAF0DE]/5 backdrop-blur p-4">
              <div className="font-display text-3xl font-black" style={{ color: s.c }}>{s.k}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FAF0DE]/40 font-mono mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tendance du jour hero */}
      {trend && (
        <Link
          to="/dashboard/trend"
          data-testid="dashboard-trend-card"
          className="card-lift block relative overflow-hidden rounded-3xl border border-[#FAF0DE]/10 bg-gradient-to-br from-[#132038] via-[#1B2B48] to-[#0A1225] mb-10 group"
        >
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FF8C42]/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#FFD600]/20 blur-3xl" />
          <div className="relative z-10 p-7 md:p-10 flex items-start gap-6">
            <div className="hidden md:flex w-14 h-14 rounded-2xl bg-[#FF8C42] items-center justify-center shrink-0 shadow-[0_0_30px_-5px_rgba(255,140,66,0.7)]">
              <Sparkles className="w-6 h-6 text-[#0A1225]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-3">
                Tendance du jour · Générée par IA
              </div>
              <h2 className="font-display text-2xl md:text-4xl font-bold leading-tight mb-4 group-hover:text-[#FFD600] transition-colors">
                {trend.headline}
              </h2>
              <p className="text-[#FAF0DE]/60 line-clamp-2 max-w-3xl">
                {trend.locked ? trend.summary : trend.summary.slice(0, 240) + "…"}
              </p>
              <div className="mt-5 text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/50 flex items-center gap-2 group-hover:text-[#FFD600] transition-colors">
                Lire la synthèse complète <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Filters row */}
      <div className="mb-8 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-5 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un article, un auteur, un mot-clé…"
            data-testid="feed-search-input"
            className="w-full bg-[#17141C]/60 border border-white/10 pl-12 pr-11 py-3.5 rounded-full text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5B4F] focus:bg-[#17141C] transition-all font-mono backdrop-blur"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              data-testid="feed-search-clear"
              className="absolute right-5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {CATEGORIES.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full whitespace-nowrap transition-all border ${
                filter === f.key
                  ? "text-black border-transparent"
                  : "text-white/70 border-white/10 hover:border-white/30"
              }`}
              style={filter === f.key ? { background: f.color } : {}}
            >
              {f.label}
            </button>
          ))}
          <span className="w-px h-6 bg-white/10 mx-1" />
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key || "all"}`}
              className={`text-xs font-mono uppercase tracking-widest px-4 py-2 rounded-full whitespace-nowrap transition-all border ${
                range === r.key
                  ? "bg-[#FFD60A] text-black border-[#FFD60A]"
                  : "text-white/60 border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {tags.slice(0, 18).map((t) => (
              <button
                key={t}
                onClick={() => setTag(tag === t ? "" : t)}
                data-testid={`tag-${t}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full border transition-all ${
                  tag === t
                    ? "bg-white text-black border-white"
                    : "text-white/50 border-white/10 hover:border-white/30 hover:text-white"
                }`}
              >
                #{t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Feed */}
      {loading ? (
        <div className="text-white/40 font-mono text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-[#17141C]/60 backdrop-blur p-12 text-center">
          <div className="font-display text-2xl font-bold mb-2">Aucun résultat</div>
          <p className="text-white/50">Essayez d'ajuster vos filtres.</p>
        </div>
      ) : showGrouped ? (
        // Kanban-style: 4 vertical columns, scroll horizontally on narrow screens
        <div className="flex gap-4 overflow-x-auto -mx-6 px-6 lg:mx-0 lg:px-0 pb-4 snap-x">
          {CATEGORIES.filter((c) => c.key !== "all").map((cat) => {
            const rows = grouped[cat.key] || [];
            return (
              <section
                key={cat.key}
                data-testid={`column-${cat.key}`}
                className="shrink-0 w-[85vw] sm:w-[380px] snap-start rounded-3xl border border-[#FAF0DE]/8 bg-[#132038]/40 backdrop-blur overflow-hidden flex flex-col"
                style={{ boxShadow: `inset 0 3px 0 ${cat.color}` }}
              >
                <div className="px-5 py-4 border-b border-[#FAF0DE]/5 flex items-center gap-3 sticky top-0 bg-[#0A1225]/80 backdrop-blur z-10">
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: `${cat.color}18`, boxShadow: `0 0 20px -8px ${cat.color}` }}
                  >
                    <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                  </span>
                  <div className="flex-1">
                    <div className="font-display text-lg font-bold leading-none" style={{ color: cat.color }}>
                      {cat.label}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#FAF0DE]/40 mt-1">
                      {rows.length} signaux
                    </div>
                  </div>
                  <button
                    onClick={() => setFilter(cat.key)}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#FAF0DE]/40 hover:text-[#FAF0DE] flex items-center gap-1"
                    data-testid={`section-${cat.key}-viewall`}
                  >
                    Voir tout <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[70vh]">
                  {rows.length === 0 ? (
                    <div className="text-center py-8 text-[#FAF0DE]/30 text-xs font-mono">
                      Aucun signal
                    </div>
                  ) : (
                    rows.slice(0, 12).map((it) => (
                      <FeedCard
                        key={it.item_id}
                        item={it}
                        color={cat.color}
                        onSubscribe={subscribe}
                        onBookmark={toggleBookmark}
                        canBookmark={!!user}
                        grid
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const cat = categoryFor(it.source_type);
            return (
              <FeedCard
                key={it.item_id}
                item={it}
                color={cat.color}
                onSubscribe={subscribe}
                onBookmark={toggleBookmark}
                canBookmark={!!user}
                grid
              />
            );
          })}
        </div>
      )}

      {!user?.subscribed && (
        <div className="mt-12 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FF8C42] to-[#FFD600] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="paywall-cta">
          <div className="flex-1 text-[#0A1225]">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] mb-2">Bienvenue</div>
            <h3 className="font-display text-2xl md:text-3xl font-black mb-2">Connectez-vous pour accéder à toute la veille.</h3>
            <p className="opacity-80">C'est entièrement gratuit pendant la phase bêta.</p>
          </div>
          <button
            onClick={subscribe}
            data-testid="dashboard-subscribe-btn"
            className="bg-[#0A1225] text-[#FAF0DE] px-6 py-3 rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#FAF0DE] hover:text-[#0A1225] transition-colors whitespace-nowrap"
          >
            Se connecter
          </button>
        </div>
      )}
    </div>
  );
}

function FeedCard({ item, color, onSubscribe, onBookmark, canBookmark, grid }) {
  if (item.locked) {
    return (
      <div
        className={`shrink-0 ${grid ? "w-full" : "w-[300px] md:w-[340px]"} snap-start rounded-2xl border border-[#FAF0DE]/8 bg-[#132038]/40 backdrop-blur p-5 flex flex-col justify-between min-h-[220px]`}
        data-testid="locked-feed-row"
      >
        <Lock className="w-5 h-5 text-[#FAF0DE]/30 mb-3" />
        <div className="text-sm text-[#FAF0DE]/40 line-clamp-3 mb-4">{item.title}</div>
        <button
          onClick={onSubscribe}
          className="text-[10px] font-mono uppercase tracking-widest text-[#FF8C42] hover:text-[#FAF0DE] self-start"
          data-testid="locked-unlock-btn"
        >
          Débloquer →
        </button>
      </div>
    );
  }
  return (
    <div
      className={`card-lift shrink-0 ${grid ? "w-full" : "w-[300px] md:w-[340px]"} snap-start rounded-2xl border border-[#FAF0DE]/8 bg-[#132038]/70 backdrop-blur overflow-hidden group hover:border-[#FAF0DE]/20 transition-all`}
      data-testid={`feed-row-${item.item_id}`}
    >
      <div className="h-1" style={{ background: color }} />
      <div className="p-5 flex flex-col h-full min-h-[220px]">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] mb-3">
          <span className="chip" style={{ color, borderColor: `${color}55`, background: `${color}12` }}>
            {item.source}
          </span>
          <span className="text-[#FAF0DE]/40 ml-auto">{timeAgo(item.published_at)}</span>
        </div>
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="block flex-1">
          <h4 className="font-display text-base md:text-lg font-bold leading-snug mb-2 group-hover:text-[#FFD600] transition-colors line-clamp-3">
            {item.title}
          </h4>
          {item.summary && (
            <p className="text-xs text-[#FAF0DE]/50 leading-relaxed line-clamp-2">{item.summary}</p>
          )}
        </a>
        <div className="mt-4 pt-3 border-t border-[#FAF0DE]/5 flex items-center justify-between">
          <div className="text-[10px] text-[#FAF0DE]/40 truncate">
            {item.author ? `par ${item.author}` : ""}
          </div>
          <div className="flex items-center gap-2">
            {canBookmark && (
              <button
                onClick={() => onBookmark(item)}
                data-testid={`bookmark-btn-${item.item_id}`}
                title={item.bookmarked ? "Retirer des favoris" : "Ajouter aux favoris"}
                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                  item.bookmarked ? "text-[#FFD600] bg-[#FFD600]/10" : "text-[#FAF0DE]/40 hover:text-[#FAF0DE] hover:bg-[#FAF0DE]/5"
                }`}
              >
                <Bookmark className={`w-3.5 h-3.5 ${item.bookmarked ? "fill-current" : ""}`} />
              </button>
            )}
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 rounded-lg flex items-center justify-center text-[#FAF0DE]/40 hover:text-[#FAF0DE] hover:bg-[#FAF0DE]/5"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
